import { Link } from "react-router-dom";
import { Button } from "@redelair-sa/intiza-ui";
import { AgendaItem, AgendaResponse } from "../../../services/AgendaService";
import { TranslationService } from "../../../services/TranslationService";
import { getLinkTo } from "../../shared/utils/LinkUtils";
import {
  formatAgendaDay,
  formatAgendaTime,
  translateWith
} from "../utils/agendaFormat";
import { AgendaProgress } from "../utils/agendaUtils";
import AgendaItemRow from "./AgendaItemRow";

export const AGENDA_FULL_ROUTE = "agenda/full";

interface AgendaTodayCardProps {
  /** Heading override; falls back to the default translation when empty. */
  title?: string;
  agenda: AgendaResponse;
  progress: AgendaProgress;
  topItems: AgendaItem[];
  canEdit: boolean;
  onRegenerate: () => void;
  onToggleCompleted: (item: AgendaItem) => void;
  onOpen: (item: AgendaItem) => void;
}

/** "My agenda for today": top pending items and a link to the full agenda. */
const AgendaTodayCard = ({
  title,
  agenda,
  progress,
  topItems,
  canEdit,
  onRegenerate,
  onToggleCompleted,
  onOpen
}: AgendaTodayCardProps) => {
  const { translate } = TranslationService;
  return (
    <div className="agenda-card">
      <div className="agenda-card__header">
        <div className="agenda-card__heading">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <h2 className="agenda-card__title">
              {title?.trim() || translate.AgendaMyAgendaToday}
            </h2>
            <span className="agenda-badge agenda-badge--ai">
              <i
                className="fa-solid fa-wand-magic-sparkles"
                aria-hidden="true"
              />{" "}
              {translate.AgendaGeneratedWithAI}
            </span>
          </div>
          <div className="agenda-card__subtitle">
            {translateWith("AgendaGeneratedAt", {
              date: formatAgendaDay(agenda.agendaDate),
              time: formatAgendaTime(agenda.generatedAt)
            })}
          </div>
        </div>
        <div className="agenda-card__tools">
          <div className="agenda-progress">
            <span className="agenda-progress__text">
              {translateWith("AgendaProgress", {
                done: progress.completed,
                total: progress.total
              })}
            </span>
            <div
              className="agenda-progress__bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progress.percent}
            >
              <div
                className="agenda-progress__fill"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
          </div>
          {canEdit && (
            <Button variant="secondary" size="sm" onClick={onRegenerate}>
              {translate.AgendaRegenerate}
            </Button>
          )}
        </div>
      </div>

      <div className="agenda-list">
        {topItems.map((item) => (
          <AgendaItemRow
            key={item.itemId}
            item={item}
            variant="summary"
            canEdit={canEdit}
            onToggleCompleted={onToggleCompleted}
            onOpen={onOpen}
          />
        ))}
      </div>

      <div className="agenda-card__footer">
        <Link to={getLinkTo(AGENDA_FULL_ROUTE)}>
          {translate.AgendaViewFull}{" "}
          <i className="fa-solid fa-arrow-right" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
};

export default AgendaTodayCard;
